/**
 * Authentication Service
 * Handles all authentication using Supabase Auth and custom broker integration.
 */

import {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  AuthUser,
} from '../types/auth.types';
import { supabase } from '../config/supabase';

/**
 * Storage keys for session management
 */
const USER_KEY = 'bol_auth_user';

/**
 * Available brokers
 */
export const BROKERS = [
  { id: 1, name: 'Haidar Logística', website: 'https://haidar.com.br' },
  { id: 2, name: 'Pibernat Despachos Aduaneiros', website: 'https://pibernat.com.br' },
  { id: 3, name: 'Wilson Sons', website: 'https://wilsonsons.com.br' },
  { id: 4, name: 'Deicmar Despachos', website: 'https://deicmar.com.br' },
];

/**
 * Get stored user data (includes broker info)
 */
export function getStoredUser(): AuthUser | null {
  try {
    const userJson = localStorage.getItem(USER_KEY);
    return userJson ? JSON.parse(userJson) : null;
  } catch (error) {
    console.error('Error retrieving stored user:', error);
    return null;
  }
}

/**
 * Store user data
 */
export function setStoredUser(user: AuthUser): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Error storing user data:', error);
  }
}

/**
 * Remove stored user data
 */
export function removeStoredUser(): void {
  try {
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error('Error removing user data:', error);
  }
}

/**
 * Get Supabase auth token
 */
export async function getAuthToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || null;
}

/**
 * Validate email domain against broker
 */
export async function validateEmailDomain(email: string): Promise<{ valid: boolean; brokerId?: string | number; error?: string }> {
  const domain = email.split('@')[1]?.toLowerCase();
  
  if (!domain) {
    return { valid: false, error: 'Invalid email format' };
  }
  
  console.log('[Auth] Validating email domain:', domain);
  
  // Query customs_brokers table to find matching broker
  // Using * to get all columns since we don't know the exact schema
  const { data: brokers, error } = await supabase
    .from('customs_brokers')
    .select('*');
  
  if (error) {
    console.error('[Auth] Database error during validation:', error);
    console.error('[Auth] Could not query customs_brokers table. Trying fallback...');
    
    // Fallback to local BROKERS list
    const broker = BROKERS.find(b => {
      const brokerDomain = b.website.toLowerCase()
        .replace('https://', '')
        .replace('http://', '')
        .replace('www.', '');
      return domain === brokerDomain || brokerDomain.includes(domain);
    });
    
    if (!broker) {
      console.error('[Auth] No matching broker found for domain:', domain);
      return {
        valid: false,
        error: `Email domain "${domain}" is not registered with any customs broker. Contact your administrator if you believe this is an error.`
      };
    }
    console.log('[Auth] Matched broker (fallback):', broker.name, 'ID:', broker.id);
    return { valid: true, brokerId: broker.id };
  }
  
  console.log('[Auth] Found brokers in database:', brokers?.length);
  if (brokers && brokers.length > 0) {
    console.log('[Auth] Sample broker record:', brokers[0]);
    console.log('[Auth] Broker columns:', Object.keys(brokers[0]));
  }
  
  // Match domain to broker website
  const broker = brokers?.find(b => {
    if (!b.website) {
      return false;
    }
    
    // Extract the base domain from the website URL
    // e.g., "https://pibernat.com.br" -> "pibernat.com.br"
    // e.g., "https://www.haidar.com.br" -> "haidar.com.br"
    const websiteDomain = b.website.toLowerCase()
      .replace('https://', '')
      .replace('http://', '')
      .replace('www.', '')
      .split('/')[0] // Remove any path
      .trim();
    
    console.log('[Auth] Comparing:', { 
      broker: b.broker_name || 'Unknown', 
      websiteDomain, 
      emailDomain: domain,
      match: websiteDomain === domain 
    });
    
    // Exact match or email domain ends with website domain
    // This handles: user@pibernat.com.br matches website: https://pibernat.com.br
    return domain === websiteDomain || domain.endsWith(websiteDomain);
  });
  
  if (!broker) {
    console.error('[Auth] No broker matched for domain:', domain);
    return {
      valid: false,
      error: `Email domain "${domain}" is not registered with any customs broker. Contact your administrator if you believe this is an error.`
    };
  }
  
  console.log('[Auth] Matched broker:', broker.broker_name, 'ID:', broker.id);
  return { valid: true, brokerId: broker.id as any };
}

/**
 * Get broker info from customs_brokers table
 */
export async function getBrokerInfo(brokerId: number | string) {
  console.log('[Auth] Getting broker info for ID:', brokerId, 'Type:', typeof brokerId);
  
  // Validate brokerId
  if (!brokerId || brokerId === 'undefined' || brokerId === 'null') {
    console.error('[Auth] Invalid broker ID provided:', brokerId);
    return null;
  }
  
  try {
    // Using * to get all columns since we don't know the exact schema
    const { data, error } = await supabase
      .from('customs_brokers')
      .select('*')
      .eq('id', brokerId)
      .single();
    
    if (error) {
      console.error('[Auth] Error fetching broker info:', error);
      console.error('[Auth] Query details - ID:', brokerId, 'Type:', typeof brokerId);
      return null;
    }
    
    console.log('[Auth] Broker info retrieved:', data);
    
    // Normalize the broker data to have consistent property names
    // The database uses 'broker_name' but our code expects 'name'
    if (data) {
      return {
        id: data.id,
        name: data.broker_name || 'Unknown Broker',
        website: data.website || ''
      };
    }
    
    return null;
  } catch (err) {
    console.error('[Auth] Exception in getBrokerInfo:', err);
    return null;
  }
}

/**
 * Register a new user
 */
export async function register(request: RegisterRequest): Promise<AuthResponse> {
  try {
    // Validate email domain
    const domainCheck = await validateEmailDomain(request.email);
    if (!domainCheck.valid) {
      return {
        success: false,
        error: domainCheck.error,
      };
    }
    
    // Register with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: request.email,
      password: request.password,
      options: {
        data: {
          broker_id: domainCheck.brokerId,
        }
      }
    });
    
    if (authError) {
      return {
        success: false,
        error: authError.message,
      };
    }
    
    if (!authData.user) {
      return {
        success: false,
        error: 'Registration failed',
      };
    }
    
    // Get broker info
    const broker = await getBrokerInfo(domainCheck.brokerId!);
    
    if (!broker) {
      return {
        success: false,
        error: 'Broker not found',
      };
    }
    
    const user: AuthUser = {
      id: authData.user.id,
      email: authData.user.email!,
      broker,
      last_login_at: new Date().toISOString(),
    };
    
    setStoredUser(user);
    
    return {
      success: true,
      user,
      token: authData.session?.access_token || '',
      message: 'Registration successful! Please check your email to verify your account.',
    };
  } catch (error: any) {
    console.error('Registration error:', error);
    return {
      success: false,
      error: error.message || 'Registration failed',
    };
  }
}

/**
 * Login user
 */
export async function login(request: LoginRequest): Promise<AuthResponse> {
  try {
    // Sign in with Supabase
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: request.email,
      password: request.password,
    });
    
    if (authError) {
      return {
        success: false,
        error: authError.message === 'Invalid login credentials' 
          ? 'Invalid email or password' 
          : authError.message,
      };
    }
    
    if (!authData.user || !authData.session) {
      return {
        success: false,
        error: 'Login failed',
      };
    }
    
    // Get broker ID from user metadata
    let brokerId = authData.user.user_metadata?.broker_id;
    
    console.log('[Auth] Login - broker_id from metadata:', brokerId, 'Type:', typeof brokerId);
    
    if (!brokerId) {
      // Try to determine from email domain
      const domainCheck = await validateEmailDomain(authData.user.email!);
      if (!domainCheck.valid || !domainCheck.brokerId) {
        return {
          success: false,
          error: 'User is not associated with a customs broker',
        };
      }
      
      // Use the brokerId from domain validation
      brokerId = domainCheck.brokerId;
      
      // Update user metadata with broker_id for future logins
      await supabase.auth.updateUser({
        data: { broker_id: domainCheck.brokerId }
      });
      
      console.log('[Auth] Updated user metadata with broker_id:', brokerId);
    }
    
    // Check if we need to update from old integer format or if metadata was set incorrectly
    // Re-validate from email domain to ensure we have the correct broker_id
    const domainCheck = await validateEmailDomain(authData.user.email!);
    if (domainCheck.valid && domainCheck.brokerId && domainCheck.brokerId !== brokerId) {
      console.log('[Auth] broker_id mismatch - updating from', brokerId, 'to', domainCheck.brokerId);
      brokerId = domainCheck.brokerId;
      
      // Update user metadata with correct broker_id
      await supabase.auth.updateUser({
        data: { broker_id: domainCheck.brokerId }
      });
      
      console.log('[Auth] Updated broker_id in metadata to:', brokerId);
    }
    
    console.log('[Auth] Using broker_id:', brokerId);
    
    // Get broker info - brokerId can be string (UUID) or number
    const broker = await getBrokerInfo(brokerId);
    
    if (!broker) {
      return {
        success: false,
        error: 'Broker not found',
      };
    }
    
    const user: AuthUser = {
      id: authData.user.id,
      email: authData.user.email!,
      broker,
      last_login_at: new Date().toISOString(),
    };
    
    setStoredUser(user);
    
    return {
      success: true,
      user,
      token: authData.session.access_token,
      message: 'Login successful',
    };
  } catch (error: any) {
    console.error('Login error:', error);
    return {
      success: false,
      error: error.message || 'Login failed',
    };
  }
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    await supabase.auth.signOut();
    removeStoredUser();
  } catch (error) {
    console.error('Logout error:', error);
    removeStoredUser(); // Clear local data even if Supabase call fails
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}

/**
 * Get current session
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const { data } = await supabase.auth.getSession();
    
    if (!data.session?.user) {
      console.log('[Auth getCurrentUser] No active session');
      return null;
    }
    
    // Check localStorage first
    const storedUser = getStoredUser();
    if (storedUser && storedUser.id === data.session.user.id) {
      console.log('[Auth getCurrentUser] Using stored user');
      return storedUser;
    }
    
    // Reconstruct from session
    let brokerId = data.session.user.user_metadata?.broker_id;
    
    console.log('[Auth getCurrentUser] broker_id from metadata:', brokerId, 'Type:', typeof brokerId);
    
    // If no broker_id in metadata, try to get it from email domain
    if (!brokerId) {
      console.log('[Auth getCurrentUser] No broker_id in metadata, validating email domain');
      const domainCheck = await validateEmailDomain(data.session.user.email!);
      if (domainCheck.valid && domainCheck.brokerId) {
        brokerId = domainCheck.brokerId;
        // Update metadata for future
        await supabase.auth.updateUser({
          data: { broker_id: domainCheck.brokerId }
        });
        console.log('[Auth getCurrentUser] Set broker_id from email domain:', brokerId);
      } else {
        console.error('[Auth] Cannot determine broker_id for user');
        // Clear corrupted session
        await logout();
        return null;
      }
    }
    
    // Always re-validate to ensure we have the correct broker_id (handles UUID/integer migration)
    console.log('[Auth getCurrentUser] Re-validating broker_id from email domain');
    const domainCheck = await validateEmailDomain(data.session.user.email!);
    if (domainCheck.valid && domainCheck.brokerId && domainCheck.brokerId !== brokerId) {
      console.log('[Auth getCurrentUser] Updating broker_id from', brokerId, 'to', domainCheck.brokerId);
      brokerId = domainCheck.brokerId;
      // Update metadata
      await supabase.auth.updateUser({
        data: { broker_id: domainCheck.brokerId }
      });
    }
    
    console.log('[Auth getCurrentUser] Using broker_id:', brokerId);
    
    // Validate broker_id before querying
    if (!brokerId || brokerId === 'undefined' || brokerId === 'null') {
      console.error('[Auth getCurrentUser] Invalid broker_id, clearing session');
      await logout();
      return null;
    }
    
    const broker = await getBrokerInfo(brokerId);
    
    if (!broker) {
      console.error('[Auth getCurrentUser] Broker not found, clearing session');
      await logout();
      return null;
    }
    
    const user: AuthUser = {
      id: data.session.user.id,
      email: data.session.user.email!,
      broker,
      last_login_at: new Date().toISOString(),
    };
    
    setStoredUser(user);
    return user;
  } catch (error) {
    console.error('[Auth getCurrentUser] Error:', error);
    // Clear corrupted data
    await logout();
    return null;
  }
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email: string): Promise<{ success: boolean; error?: string; message?: string }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      return {
        success: false,
        error: error.message,
      };
    }
    
    return {
      success: true,
      message: 'Password reset email sent. Please check your inbox.',
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to send reset email',
    };
  }
}

/**
 * Reset password with token
 */
export async function resetPassword(newPassword: string): Promise<{ success: boolean; error?: string; message?: string }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (error) {
      return {
        success: false,
        error: error.message,
      };
    }
    
    return {
      success: true,
      message: 'Password updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to reset password',
    };
  }
}
